/**
 * Supabase Client Configuration
 *
 * This file initializes the Supabase client for database operations.
 * Supabase provides a PostgreSQL database with real-time capabilities.
 *
 * Setup Instructions:
 * 1. Create a project at https://supabase.com/dashboard
 * 2. Go to Settings > API
 * 3. Copy the Project URL and anon/public key
 * 4. Add to .env:
 *    REACT_APP_SUPABASE_URL=https://xxxxx.supabase.co
 *    REACT_APP_SUPABASE_ANON_KEY=eyJhbG...
 */

import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || '';
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || '';

// Initialize Supabase client
export const supabase = createClient(supabaseUrl, supabaseAnonKey);

/**
 * Check if Supabase is properly configured
 */
export const isSupabaseConfigured = () => {
  return Boolean(supabaseUrl && supabaseAnonKey);
};

/**
 * Get user subscription from database
 * @param {string} auth0Id - Auth0 user ID (from user.sub)
 * @param {string} email - User email (fallback lookup)
 * @returns {Promise<Object|null>} Subscription object or null
 */
export const getUserSubscription = async (auth0Id, email = null) => {
  try {
    // First try to find by auth0_id
    let { data, error } = await supabase
      .from('subscriptions')
      .select('*')
      .eq('auth0_id', auth0Id)
      .eq('status', 'active')
      .single();

    if (error && error.code === 'PGRST116' && email) {
      // Not found by auth0_id, try by email
      console.log('Subscription not found by auth0_id, trying email:', email);
      const result = await supabase
        .from('subscriptions')
        .select('*')
        .eq('email', email)
        .eq('status', 'active')
        .single();

      data = result.data;
      error = result.error;

      // If found by email, update the record with the correct auth0_id
      if (data && !error) {
        console.log('Found subscription by email, updating auth0_id');
        await supabase
          .from('subscriptions')
          .update({ auth0_id: auth0Id })
          .eq('id', data.id);

        data.auth0_id = auth0Id; // Update local copy
      }
    }

    if (error) {
      if (error.code === 'PGRST116') {
        // No subscription found - this is okay
        return null;
      }
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Error fetching subscription:', error);
    return null;
  }
};

/**
 * Check if user has an active subscription
 * @param {string} auth0Id - Auth0 user ID
 * @param {string} email - User email (fallback lookup)
 * @returns {Promise<boolean>} True if user has active subscription
 */
export const hasActiveSubscription = async (auth0Id, email = null) => {
  const subscription = await getUserSubscription(auth0Id, email);

  if (!subscription) {
    return false;
  }

  // Check if subscription is still valid (not expired)
  const now = new Date();
  const endDate = new Date(subscription.current_period_end);

  return subscription.status === 'active' && endDate > now;
};

/**
 * Create or update subscription record
 * @param {Object} subscriptionData - Subscription data from Stripe
 * @returns {Promise<Object>} Created/updated subscription
 */
export const upsertSubscription = async (subscriptionData) => {
  try {
    const { data, error } = await supabase
      .from('subscriptions')
      .upsert(subscriptionData, {
        onConflict: 'stripe_subscription_id'
      })
      .select()
      .single();

    if (error) throw error;

    return data;
  } catch (error) {
    console.error('Error upserting subscription:', error);
    throw error;
  }
};

/**
 * Cancel subscription (mark as cancelled)
 * @param {string} stripeSubscriptionId - Stripe subscription ID
 * @returns {Promise<Object>} Updated subscription
 */
export const cancelSubscription = async (stripeSubscriptionId) => {
  try {
    const { data, error } = await supabase
      .from('subscriptions')
      .update({
        status: 'canceled',
        canceled_at: new Date().toISOString()
      })
      .eq('stripe_subscription_id', stripeSubscriptionId)
      .select()
      .single();

    if (error) throw error;

    return data;
  } catch (error) {
    console.error('Error canceling subscription:', error);
    throw error;
  }
};

/**
 * Log user signup for tracking
 * @param {Object} signupData - Signup information
 * @returns {Promise<Object>} Created signup record
 */
export const logUserSignup = async (signupData) => {
  try {
    const { data, error } = await supabase
      .from('user_signups')
      .insert({
        auth0_id: signupData.auth0_id,
        signup_email: signupData.signup_email,
        auth_method: signupData.auth_method,
        checkout_email: signupData.checkout_email || null,
        stripe_session_id: signupData.stripe_session_id || null,
        emails_match: signupData.checkout_email
          ? signupData.signup_email.toLowerCase() === signupData.checkout_email.toLowerCase()
          : null,
        user_agent: navigator.userAgent,
        subscription_found: signupData.subscription_found || false,
        notes: signupData.notes || null
      })
      .select()
      .single();

    if (error) throw error;

    console.log('Signup logged:', data);
    return data;
  } catch (error) {
    console.error('Error logging signup:', error);
    return null;
  }
};

/**
 * Check if user signup has been logged
 * @param {string} auth0Id - Auth0 user ID
 * @returns {Promise<boolean>} True if already logged
 */
export const hasSignupBeenLogged = async (auth0Id) => {
  try {
    const { data, error } = await supabase
      .from('user_signups')
      .select('id')
      .eq('auth0_id', auth0Id)
      .single();

    if (error && error.code === 'PGRST116') {
      return false; // Not found
    }

    return Boolean(data);
  } catch (error) {
    console.error('Error checking signup log:', error);
    return false;
  }
};

export default supabase;
